import { useId, useState } from "react"
import type { ReactNode } from "react"
import Button from "@/components/Button/Button"
import { DECK_PREVIEW_GATE } from "@/content/deckPreviewGate"
import styles from "@/styles/components/DeckPreviewGate.module.css"

/*
 * A password screen in front of the public deck preview (`/preview`).
 *
 * This is a SOFT deterrent, NOT real security. The preview is a static app, so
 * both the password below and the deck content itself ship in the bundle and
 * can be read or fetched directly by anyone technical — the gate only keeps a
 * casual visitor off the URL. Do not treat it as access control, and do not
 * reuse the value below for anything that actually has to be secret. Real
 * protection would have to live off the client (hosting/CDN auth or a server
 * that checks the password before serving the deck).
 */

// Ships in the client bundle in plaintext — intentionally NOT a secret.
const PASSWORD = "Loka!2026"
const STORAGE_KEY = "loka.deckPreview.unlocked"

/*
 * sessionStorage, not localStorage: the unlock survives reloads and in-tab
 * navigation but clears when the tab or browser closes, so a shared machine is
 * not left open on the deck forever. Wrapped because storage throws in private
 * mode / when disabled.
 */
function readUnlocked() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === "1"
  } catch {
    return false
  }
}

interface DeckPreviewGateProps {
  children: ReactNode
}

export default function DeckPreviewGate({ children }: DeckPreviewGateProps) {
  const [unlocked, setUnlocked] = useState(readUnlocked)
  const [value, setValue] = useState("")
  const [error, setError] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const fieldId = useId()
  const errorId = useId()

  if (unlocked) return <>{children}</>

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (value === PASSWORD) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, "1")
      } catch {
        // Storage disabled: unlock for this render anyway — the gate just
        // reappears on reload, which is acceptable for a soft deterrent.
      }
      setUnlocked(true)
      return
    }
    // No lockout or attempt counting: pointless on a gate anyone can bypass by
    // reading the bundle.
    setError(true)
    setValue("")
  }

  return (
    <div className={styles.gate}>
      <form className={styles.card} onSubmit={submit}>
        <h1 className={styles.title}>{DECK_PREVIEW_GATE.title}</h1>
        <p className={styles.blurb}>{DECK_PREVIEW_GATE.blurb}</p>

        <label className={styles.label} htmlFor={fieldId}>
          {DECK_PREVIEW_GATE.label}
        </label>
        <div className={styles.inputWrap}>
          <input
            id={fieldId}
            className={styles.input}
            type={revealed ? "text" : "password"}
            value={value}
            autoFocus
            autoComplete="current-password"
            aria-invalid={error || undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => {
              setValue(event.target.value)
              if (error) setError(false)
            }}
          />
          {/* type="button" so it never submits the form. The icon shows the
              action, not the state: an eye to reveal, an eye with a slash to
              hide. */}
          <button
            type="button"
            className={styles.reveal}
            onClick={() => setRevealed((shown) => !shown)}
            aria-label={revealed ? DECK_PREVIEW_GATE.hide : DECK_PREVIEW_GATE.show}
            aria-pressed={revealed}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
              {revealed ? (
                <path
                  d="M3 3L17 17"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              ) : null}
            </svg>
          </button>
        </div>

        {error ? (
          <p id={errorId} className={styles.error} role="alert">
            {DECK_PREVIEW_GATE.error}
          </p>
        ) : null}

        <Button type="submit" className={styles.submit} disabled={value.length === 0}>
          {DECK_PREVIEW_GATE.submit}
        </Button>
      </form>
    </div>
  )
}
